/**
 * WizardStepEntry (Step 1)
 *
 * Entry point for the unified wizard.
 * Asks "What are you working on?" with two options:
 * - Single Property Permit
 * - Development Project
 */

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FileText, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WizardMode } from '@/types/wizard';

interface WizardStepEntryProps {
  selectedMode: WizardMode;
  onSelectMode: (mode: WizardMode) => void;
}

interface ModeOption {
  mode: WizardMode;
  title: string;
  description: string;
  examples: string[];
  icon: React.ReactNode;
  badge?: string;
}

const MODE_OPTIONS: ModeOption[] = [
  {
    mode: 'single_permit',
    title: 'Single Property Permit',
    description: 'Pull a permit for one property or address',
    examples: [
      'Kitchen remodel',
      'Panel upgrade',
      'Roof replacement',
      'HVAC changeout',
      'Pool installation',
    ],
    icon: <FileText className="h-8 w-8" />,
  },
  {
    mode: 'development_project',
    title: 'Development Project',
    description: 'Manage permits across multiple units or lots',
    examples: [
      'Residential subdivision',
      'Townhome community',
      'Apartment complex',
      'Mixed-use development',
      'Commercial complex',
    ],
    icon: <Building2 className="h-8 w-8" />,
    badge: 'Multi-Unit',
  },
];

export function WizardStepEntry({ selectedMode, onSelectMode }: WizardStepEntryProps) {
  return (
    <div className="space-y-6">
      {/* Question */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">What are you working on?</h2>
        <p className="text-muted-foreground">
          Select the type of project to get started
        </p>
      </div>

      {/* Options */}
      <div className="grid gap-4 md:grid-cols-2">
        {MODE_OPTIONS.map((option) => {
          const isSelected = selectedMode === option.mode;

          return (
            <Card
              key={option.mode}
              className={cn(
                'relative cursor-pointer transition-all border-border bg-card/40 hover:bg-muted/40 hover:border-foreground/20'
              )}
              onClick={() => onSelectMode(option.mode)}
              data-selected={isSelected ? 'true' : 'false'}
            >
              <CardContent className="p-6">
                {/* Icon and Badge */}
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className={cn(
                      'p-3 rounded-lg bg-muted text-muted-foreground'
                    )}
                  >
                    {option.icon}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg">{option.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {option.description}
                    </p>
                    {option.badge && (
                      <span className="mt-2 inline-flex items-center rounded-full border border-muted-foreground/30 bg-muted/50 px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        {option.badge}
                      </span>
                    )}
                  </div>
                </div>

                {/* Examples */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Examples
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {option.examples.map((example) => (
                      <Badge
                        key={example}
                        variant="outline"
                        className="text-xs font-normal"
                      >
                        {example}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help text */}
      <p className="text-center text-sm text-muted-foreground">
        Not sure? Start with <strong>Single Property Permit</strong> for most projects.
        <br />
        Development Project is for subdivisions, townhomes, and multi-building developments.
      </p>
    </div>
  );
}
